/**
 * Fantasy Console 0 - Sprite Sheet Picker Component
 *
 * Displays the 128×128 sprite sheet as a grid of 256 sprites.
 * Used by both the sprite editor (for selecting sprites to edit)
 * and the map editor (for selecting tiles to place).
 */

import { useRef, useEffect, useCallback } from 'react'
import { SPRITE_SHEET_SIZE, SPRITE_SIZE } from '@fc0/types'
import {
  PICO8_RGB,
  getSpriteIndexFromPosition,
  getSpriteCoords,
} from '../../utils/spriteUtils'
import styles from './SpriteSheetPicker.module.css'
import editorStyles from '../../styles/editor.module.css'

interface SpriteSheetPickerProps {
  sprites: Uint8Array
  selectedIndex: number
  onSelect: (index: number) => void
  scale?: number
  label: string
}

export function SpriteSheetPicker({
  sprites,
  selectedIndex,
  onSelect,
  scale = 2,
  label,
}: SpriteSheetPickerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Render the sprite sheet to canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const canvasSize = SPRITE_SHEET_SIZE * scale
    canvas.width = canvasSize
    canvas.height = canvasSize

    // Create image data at 1:1 scale
    const imageData = ctx.createImageData(SPRITE_SHEET_SIZE, SPRITE_SHEET_SIZE)

    for (let i = 0; i < sprites.length; i++) {
      const colorIndex = sprites[i]
      const [r, g, b] = PICO8_RGB[colorIndex]
      const pixelIndex = i * 4
      imageData.data[pixelIndex] = r
      imageData.data[pixelIndex + 1] = g
      imageData.data[pixelIndex + 2] = b
      imageData.data[pixelIndex + 3] = 255
    }

    // Draw at 1:1 on temp canvas, then scale up
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = SPRITE_SHEET_SIZE
    tempCanvas.height = SPRITE_SHEET_SIZE
    const tempCtx = tempCanvas.getContext('2d')
    if (tempCtx) {
      tempCtx.putImageData(imageData, 0, 0)
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(tempCanvas, 0, 0, canvasSize, canvasSize)
    }

    // Draw selection highlight
    const { x, y } = getSpriteCoords(selectedIndex)
    const selX = x * scale
    const selY = y * scale
    const selSize = SPRITE_SIZE * scale

    ctx.strokeStyle = '#29adff'
    ctx.lineWidth = 2
    ctx.strokeRect(selX + 1, selY + 1, selSize - 2, selSize - 2)
  }, [sprites, selectedIndex, scale])

  // Handle click to select sprite
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      const index = getSpriteIndexFromPosition(
        x,
        y,
        canvas.width,
        canvas.height,
      )
      onSelect(index)
    },
    [onSelect],
  )

  const canvasSize = SPRITE_SHEET_SIZE * scale

  return (
    <div className={editorStyles.panelContainer}>
      <div className={editorStyles.panelHeader}>
        <span className={editorStyles.panelLabel}>{label}</span>
        <span className={editorStyles.panelInfo}>
          #{selectedIndex.toString().padStart(3, '0')}
        </span>
      </div>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        style={{ width: canvasSize, height: canvasSize }}
        onClick={handleClick}
        tabIndex={0}
        role='grid'
        aria-label={`${label} grid`}
      />
    </div>
  )
}
